























































































































































































import { Component, Prop } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { ExStore } from 'vuex'
import dayjs from 'dayjs'
import { getAllDatesOfMonth } from '@/utils/datetime'
import Form from '@/mixins/form'
import {
  DispensationPayload,
  MedicineItemPayload
} from '@/api/dispensation/types'
import { DateString } from '@/types/utils'

export type InputeMedicineItem = MedicineItemPayload
export type InputeMedicineList = Array<InputeMedicineItem>
export type InputMedicineGroupList = {
  [rpNo: number]: Array<InputeMedicineItem>
}

const TODAY = dayjs()
const YESTERDAY = dayjs().subtract(1, 'day')
const TODAY_OF_LAST_YEAR = TODAY.subtract(1, 'year')
const TODAY_OF_NEXT_YEAR = TODAY.add(1, 'year')
const FIRST_RECIPE_NUMBER = 1

const defaultInputMedicine = (
  rpNo: number,
  seq: number,
  isOnedose = false,
  usage = ''
): InputeMedicineItem => ({
  seq,
  rpNo,
  isOnedose,
  usage,
  medicineCode: '',
  medicineName: '',
  dose: 0,
  doseUnit: '',
  daysTimes: 0
})

export const makeMedicineGroups = ({ medicines }: DispensationPayload) => {
  const groups = medicines.reduce((groups, medicine) => {
    if (!groups[medicine.rpNo]) {
      groups[medicine.rpNo] = []
    }

    groups[medicine.rpNo].push({ ...medicine })

    return groups
  }, {} as InputMedicineGroupList)

  Object.keys(groups).forEach(rpNo => {
    groups[Number(rpNo)].sort((m1, m2) => m1.seq - m2.seq)
  })

  return groups
}

@Component({
  components: {
    TextInput: () => import('@/components/common/inputs/TextInput.vue'),
    SelectInput: () => import('@/components/common/inputs/SelectInput.vue'),
    MedicineInputGroup: () => import('./MedicineInputGroup.vue')
  }
})
export default class DispensationCreateForm extends mixins(Form) {
  @Prop(Number) patientId!: number
  @Prop(Object) dispensation!: DispensationPayload | null

  $store!: ExStore

  inputPrescriptionYear: null | number = YESTERDAY.year()
  inputPrescriptionMonth: null | number = YESTERDAY.month() + 1
  inputPrescriptionDay: null | number = YESTERDAY.date()

  inputDispensationYear = TODAY.year()
  inputDispensationMonth = TODAY.month() + 1
  inputDispensationDay = TODAY.date()
  inputMedicines: InputMedicineGroupList = {
    [FIRST_RECIPE_NUMBER]: [defaultInputMedicine(1, 1)]
  }

  inputMedicationType = ''
  inputPrescriptionInstitutionName = ''
  inputPrescriptionDoctorName = ''
  inputPrescriptionDepartment = ''

  errorMessage = ''

  get isSelectPrescriptionDate() {
    return (
      this.inputPrescriptionYear !== null,
      this.inputPrescriptionMonth !== null,
      this.inputPrescriptionDay !== null
    )
  }

  get selectedPrescriptionDate() {
    if (!this.isSelectPrescriptionDate) {
      return null
    }

    return dayjs(
      new Date(
        this.inputPrescriptionYear as number,
        (this.inputPrescriptionMonth as number) - 1,
        this.inputPrescriptionDay as number
      )
    )
  }

  get selectedDispensationDate() {
    return dayjs(
      new Date(
        this.inputDispensationYear,
        this.inputDispensationMonth - 1,
        this.inputDispensationDay
      )
    )
  }

  get medicationTypeOptions() {
    return [
      {
        value: '',
        text: '種別を選択'
      },
      {
        value: '臨時',
        text: '臨時'
      },
      {
        value: '持参薬',
        text: '持参薬'
      },
      {
        value: '定期',
        text: '定期'
      }
    ]
  }

  get recipeNumbers() {
    return Object.keys(this.inputMedicines)
      .map(Number)
      .sort((r1, r2) => r1 - r2)
  }

  get yearOptions() {
    const start = TODAY_OF_LAST_YEAR.year()
    const end = TODAY_OF_NEXT_YEAR.year()
    return [...new Array(end - start + 1)].map((_, i) => start + i)
  }

  get prescriptionMonthOptions() {
    if (!this.inputPrescriptionYear) {
      return []
    }
    return this.generateMonthOptions(this.inputPrescriptionYear)
  }

  get dispensationMonthOptions() {
    return this.generateMonthOptions(this.inputDispensationYear)
  }

  get prescriptionDayOptions() {
    if (!this.inputPrescriptionYear || !this.inputPrescriptionMonth) {
      return []
    }
    return getAllDatesOfMonth(
      this.inputPrescriptionYear,
      this.inputPrescriptionMonth
    )
  }

  get dispensationDayOptions() {
    return getAllDatesOfMonth(
      this.inputDispensationYear,
      this.inputDispensationMonth
    )
  }

  get remainingOnlyOneItem() {
    const rpNumbers = Object.keys(this.inputMedicines)

    if (rpNumbers.length > 1) {
      return false
    }

    return this.inputMedicines[Number(rpNumbers[0])].length === 1
  }

  isDispensationDateGreaterThanPrescriptionDate() {
    if (!this.selectedPrescriptionDate) {
      return true
    }

    return (
      this.selectedDispensationDate.unix() >=
      this.selectedPrescriptionDate.unix()
    )
  }

  generateMonthOptions(selectedYear: number) {
    const lastYear = TODAY_OF_LAST_YEAR.year()
    const nextYear = TODAY_OF_NEXT_YEAR.year()
    const thisMonth = TODAY.month() + 1

    if (selectedYear === lastYear) {
      return [...new Array(12 - thisMonth)].map((_, i) => i + thisMonth + 1)
    }

    if (selectedYear === nextYear) {
      return [...new Array(thisMonth)].map((_, i) => i + 1)
    }

    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
  }

  buildFormPayload(): DispensationPayload {
    const prescriptionDate =
      this.selectedPrescriptionDate &&
      (this.selectedPrescriptionDate.format('YYYY-MM-DD') as DateString)
    const dispensationDate = this.selectedDispensationDate.format(
      'YYYY-MM-DD'
    ) as DateString
    const medicationType = this.inputMedicationType
    const prescriptionInstitutionName = this.inputPrescriptionInstitutionName
    const prescriptionDoctorName = this.inputPrescriptionDoctorName
    const prescriptionDepartment = this.inputPrescriptionDepartment
    const medicines = Object.keys(this.inputMedicines).reduce(
      (medicines, rpNo) => medicines.concat(this.inputMedicines[Number(rpNo)]),
      [] as Array<InputeMedicineItem>
    )

    return {
      prescriptionDate,
      dispensationDate,
      medicationType,
      prescriptionInstitutionName,
      prescriptionDoctorName,
      prescriptionDepartment,
      medicines
    }
  }

  handleChangeDispensationYear() {
    if (
      this.dispensationMonthOptions.indexOf(this.inputDispensationMonth) === -1
    ) {
      this.inputDispensationMonth = this.dispensationMonthOptions[0]
      this.inputDispensationDay = this.dispensationDayOptions[0]
    } else if (
      this.dispensationDayOptions.indexOf(this.inputDispensationDay) === -1
    ) {
      this.inputDispensationDay = this.dispensationDayOptions[0]
    }
  }

  handleChangeDispensationMonth() {
    if (this.dispensationDayOptions.indexOf(this.inputDispensationDay) === -1) {
      this.inputDispensationDay = this.dispensationDayOptions[0]
    }
  }

  handleChangePrescriptionYear(value: number | null) {
    if (value === null) {
      this.inputPrescriptionMonth = null
      this.inputPrescriptionDay = null
    } else if (
      !this.inputPrescriptionMonth ||
      this.prescriptionMonthOptions.indexOf(this.inputPrescriptionMonth) === -1
    ) {
      this.inputPrescriptionMonth = this.prescriptionMonthOptions[0]
      this.inputPrescriptionDay = this.prescriptionDayOptions[0]
    } else if (
      !this.inputPrescriptionDay ||
      this.prescriptionDayOptions.indexOf(this.inputPrescriptionDay) === -1
    ) {
      this.inputPrescriptionDay = this.prescriptionDayOptions[0]
    }
  }

  handleChangePrescriptionMonth(value: number | null) {
    if (value === null) {
      this.inputPrescriptionDay = null
    } else if (
      !this.inputPrescriptionDay ||
      this.prescriptionDayOptions.indexOf(this.inputPrescriptionDay) === -1
    ) {
      this.inputPrescriptionDay = this.prescriptionDayOptions[0]
    }
  }

  handleAddMedicineItem(rpNo: number, index: number) {
    if (!Array.isArray(this.inputMedicines[rpNo])) {
      console.error('rpNo is not existent in inputMedicines')
      return
    }

    if (!this.inputMedicines[rpNo][index]) {
      console.error('index is not existent in inputMedicines[rpNo]')
      return
    }

    const length = this.inputMedicines[rpNo].length
    const { isOnedose, usage } = this.inputMedicines[rpNo][0]

    if (length - 1 === index) {
      this.inputMedicines[rpNo].push(
        defaultInputMedicine(rpNo, length + 1, isOnedose, usage)
      )
    } else {
      for (let i = index + 1; i < length; i++) {
        const medicine = this.inputMedicines[rpNo][i]
        this.$set(medicine, 'seq', medicine.seq + 1)
      }
      this.inputMedicines[rpNo].splice(
        index + 1,
        0,
        defaultInputMedicine(rpNo, index + 2, isOnedose, usage)
      )
    }
  }

  handleRemoveMedicineItem(rpNo: number, index: number) {
    if (!Array.isArray(this.inputMedicines[rpNo])) {
      console.error('rpNo is not existent in inputMedicines')
      return
    }

    if (!this.inputMedicines[rpNo][index]) {
      console.error('index is not existent in inputMedicines[rpNo]')
      return
    }

    if (this.remainingOnlyOneItem) {
      return
    }

    if (this.inputMedicines[rpNo].length > 1) {
      this.inputMedicines[rpNo].splice(index, 1)
    } else {
      const newMedicines: InputMedicineGroupList = {}
      this.$delete(this.inputMedicines, rpNo)
      this.recipeNumbers.map(i => {
        const len = Object.keys(newMedicines).length + 1
        this.inputMedicines[i].map(m => (m.rpNo = len))
        newMedicines[len] = this.inputMedicines[i]
      })
      this.inputMedicines = newMedicines
    }
  }

  handleAddRecipeItem() {
    const rpNo = Object.keys(this.inputMedicines).length + 1
    this.$set(this.inputMedicines, rpNo, [defaultInputMedicine(rpNo, 1)])
  }

  async handleClickConfirmButton() {
    if (this.validator.hasAnyError()) {
      this.errorMessage = this.validator.getFirstErrorMessage()
      return
    }

    if (!this.isDispensationDateGreaterThanPrescriptionDate()) {
      this.errorMessage = '処方日は調剤日よりも前の日付を入力してください。'
      return
    }

    this.errorMessage = ''

    const payload = this.buildFormPayload()

    this.$emit('confirm', payload)
  }

  created() {
    const dispensation = this.dispensation

    if (dispensation) {
      if (dispensation.prescriptionDate) {
        const prescriptionDate = dayjs(dispensation.prescriptionDate)
        this.inputPrescriptionYear = prescriptionDate.year()
        this.inputPrescriptionMonth = prescriptionDate.month() + 1
        this.inputPrescriptionDay = prescriptionDate.date()
      } else {
        this.inputPrescriptionYear = null
        this.inputPrescriptionMonth = null
        this.inputPrescriptionDay = null
      }

      const dispensationDate = dayjs(dispensation.dispensationDate)
      this.inputDispensationYear = dispensationDate.year()
      this.inputDispensationMonth = dispensationDate.month() + 1
      this.inputDispensationDay = dispensationDate.date()

      this.inputMedicationType = dispensation.medicationType
      this.inputPrescriptionInstitutionName =
        dispensation.prescriptionInstitutionName
      this.inputPrescriptionDoctorName = dispensation.prescriptionDoctorName
      this.inputPrescriptionDepartment = dispensation.prescriptionDepartment
      this.inputMedicines = makeMedicineGroups(dispensation)
    }
  }
}
